import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import BikeSlidingSection from './homesections/BikeSlidingSection';
import CarSlidingSection from './homesections/CarSlidingSection';

// ── Design Tokens ──────────────────────────────────────────────────────────
const T = {
  ink:     '#0D0D0F',
  ink2:    '#1A1A20',
  slate:   '#2D2D38',
  muted:   '#6B6B7A',
  line:    '#E8E8F0',
  surface: '#F5F5FA',
  white:   '#FFFFFF',
  lime:    '#C8F23C',
  limeD:   '#A8D020',
  electric:'#3D5AFE',
  teal:    '#00BFA5',
  warn:    '#FF6D3A',
};

const BASE_URL = import.meta.env.VITE_BASE_URL || 'http://localhost:8081';

const DEFAULT_SVG = `data:image/svg+xml,${encodeURIComponent(
  `<svg width="400" height="300" xmlns="http://www.w3.org/2000/svg">
    <rect width="400" height="300" fill="#1A1A20"/>
    <text x="200" y="155" font-family="sans-serif" font-size="18" fill="#C8F23C" text-anchor="middle">City</text>
  </svg>`
)}`;

const BIKE_PACKAGES = [
  { id:1, name:'Yamaha R15',       image:'/bike1.jpg',     packs:{ '7':{ price:4080,save:867,day:583 }, '15':{ price:8400,save:1386,day:560 }, '30':{ price:16200,save:2700,day:540 } } },
  { id:2, name:'Electric Scooter', image:'/ether.jpeg',    packs:{ '7':{ price:3192,save:543,day:456 }, '15':{ price:6936,save:1176,day:462 }, '30':{ price:13440,save:2304,day:448 } } },
  { id:3, name:'Splendor Plus',    image:'/splendor.jpeg', packs:{ '7':{ price:2940,save:499,day:420 }, '15':{ price:6048,save:1029,day:403 }, '30':{ price:11760,save:2016,day:392 } } },
  { id:4, name:'Activa 6G',        image:'/activa.jpg',    packs:{ '7':{ price:3360,save:571,day:480 }, '15':{ price:7140,save:1211,day:476 }, '30':{ price:13800,save:2346,day:460 } } },
];

const WHY = [
  { icon:'⚡', title:'Instant Booking',  sub:'Under 60 seconds' },
  { icon:'🛡️', title:'Fully Insured',    sub:'Zero hidden risk' },
  { icon:'📍', title:'4 Pune Hubs',      sub:'Pickup near you' },
  { icon:'💰', title:'Price Match',      sub:'Best rate guarantee' },
  { icon:'🔧', title:'Serviced Fleet',   sub:'100% roadworthy' },
  { icon:'🕐', title:'24/7 Support',     sub:'Always reachable' },
];

const STEPS = [
  { n:'01', label:'Pick City',   desc:'Choose from our Pune hubs' },
  { n:'02', label:'Set Dates',   desc:'Pickup & drop-off times' },
  { n:'03', label:'Choose Bike', desc:'Browse available models' },
  { n:'04', label:'Pay & Ride',  desc:'Secure checkout, hit road' },
];

const REVIEWS = [
  { name:'Rahul S.',  loc:'Hinjewadi',     txt:'R15 was spotless — booking took literally 2 minutes. Vegatas is the real deal.', stars:5, ago:'2w' },
  { name:'Priya M.',  loc:'Koregaon Park', txt:'Transparent pricing, zero drama. Picked up the Activa, returned it, got my deposit back same day.', stars:5, ago:'1mo' },
  { name:'Amit K.',   loc:'Wakad',         txt:'Monthly scooter at ₹440/day? Nowhere else in Pune comes close. My daily commute is sorted.', stars:5, ago:'3w' },
  { name:'Sneha P.',  loc:'Kothrud',       txt:'The Kothrud hub is right next to my office. Staff was super helpful. Booked 3 times already.', stars:5, ago:'5d' },
  { name:'Vikram D.', loc:'Hadapsar',      txt:'Bikes are consistently well maintained. I can tell they actually service them between rentals.', stars:5, ago:'2mo' },
  { name:'Neha J.',   loc:'Baner',         txt:'Weekend trips on an electric scooter — smooth, silent, zero fuel stress. Love it.', stars:5, ago:'1w' },
];

// ── Helpers ────────────────────────────────────────────────────────────────
function getCurrentDT() {
  const now = new Date();
  const buf = new Date(now.getTime() + 15 * 60000);
  buf.setMinutes(Math.ceil(buf.getMinutes() / 5) * 5, 0, 0);
  const pad = n => String(n).padStart(2,'0');
  return {
    currentDate: `${now.getFullYear()}-${pad(now.getMonth()+1)}-${pad(now.getDate())}`,
    minTime: `${pad(buf.getHours())}:${pad(buf.getMinutes())}`,
    now, buf,
  };
}

function fmtDate(d) {
  if (!d) return '';
  return new Date(d + 'T00:00:00').toLocaleDateString('en-IN',{ day:'2-digit', month:'short', year:'numeric' });
}
function fmtTime(t) {
  if (!t) return '';
  const [h,m] = t.split(':');
  return new Date(2000,0,1,+h,+m).toLocaleTimeString('en-US',{ hour:'numeric', minute:'2-digit', hour12:true });
}

const MONTH_LONG = ['January','February','March','April','May','June','July','August','September','October','November','December'];

// ── Full-screen modal wrapper (mobile-safe) ────────────────────────────────
function BottomSheetModal({ onClose, title, children }) {
  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = ''; };
  }, []);

  return (
    <div
      onClick={onClose}
      style={{
        position:'fixed', inset:0, zIndex:2000,
        background:'rgba(13,13,15,.7)', backdropFilter:'blur(6px)',
        display:'flex', alignItems:'flex-end', justifyContent:'center',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background:T.white, borderRadius:'20px 20px 0 0',
          width:'100%', maxWidth:540,
          maxHeight:'90vh', display:'flex', flexDirection:'column',
          boxShadow:'0 -20px 60px rgba(0,0,0,.25)',
          animation:'sheetUp .28s cubic-bezier(.34,1.2,.64,1) both',
        }}
      >
        {/* Handle */}
        <div style={{ display:'flex', justifyContent:'center', padding:'12px 0 4px' }}>
          <div style={{ width:40, height:4, borderRadius:4, background:T.line }}/>
        </div>
        {/* Header */}
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'8px 20px 12px', borderBottom:`1px solid ${T.line}` }}>
          <span style={{ fontWeight:800, fontSize:16, color:T.ink }}>{title}</span>
          <button onClick={onClose} style={{ border:'none', background:'none', fontSize:22, color:T.muted, cursor:'pointer', lineHeight:1 }}>✕</button>
        </div>
        {/* Content */}
        <div style={{ overflowY:'auto', WebkitOverflowScrolling:'touch', flex:1 }}>
          {children}
        </div>
      </div>
    </div>
  );
}

// ── Calendar Picker ────────────────────────────────────────────────────────
function CalendarPicker({ onSelect, minDate, current, onClose }) {
  const { now } = getCurrentDT();
  const [month, setMonth] = useState(now.getMonth());
  const [year,  setYear]  = useState(now.getFullYear());

  const firstDay = new Date(year, month, 1).getDay();
  const lastDay  = new Date(year, month+1, 0).getDate();
  const cells = [];
  for (let i = 0; i < firstDay; i++) cells.push(null);
  for (let d = 1; d <= lastDay; d++) {
    const s = `${year}-${String(month+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
    cells.push({ d, s, past: s < (minDate || ''), sel: s === current });
  }

  const prev = () => { if (month===0){setMonth(11);setYear(y=>y-1);}else setMonth(m=>m-1); };
  const next = () => { if (month===11){setMonth(0);setYear(y=>y+1);}else setMonth(m=>m+1); };

  return (
    <div style={{ padding:'16px 16px 24px' }}>
      {/* Nav */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
        <button onClick={prev} style={navBtn}>‹</button>
        <span style={{ fontWeight:800, fontSize:15, color:T.ink }}>{MONTH_LONG[month]} {year}</span>
        <button onClick={next} style={navBtn}>›</button>
      </div>
      {/* Day headers */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4, marginBottom:6 }}>
        {['Su','Mo','Tu','We','Th','Fr','Sa'].map((d,i)=>(
          <div key={i} style={{ textAlign:'center', fontSize:11, fontWeight:700, color:T.muted, padding:'4px 0' }}>{d}</div>
        ))}
      </div>
      {/* Cells */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:4 }}>
        {cells.map((c,i) => c === null ? <div key={i}/> : (
          <button key={i} disabled={c.past}
            onClick={() => { if (!c.past) { onSelect(c.s); onClose(); } }}
            style={{
              border:'none', borderRadius:10, padding:'10px 0', fontSize:14,
              fontWeight: c.sel ? 800 : 500,
              cursor: c.past ? 'not-allowed' : 'pointer',
              background: c.sel ? T.ink : 'transparent',
              color: c.sel ? T.lime : c.past ? '#D0D0DC' : T.ink,
              transition:'background .15s',
            }}
          >{c.d}</button>
        ))}
      </div>
    </div>
  );
}

const navBtn = {
  background:'none', border:'none', cursor:'pointer',
  fontSize:22, color:T.ink, padding:'4px 12px', borderRadius:8,
};

// ── Time List ──────────────────────────────────────────────────────────────
function TimeList({ onSelect, current, forDate, minDate, minTime, pickupDate, pickupTime, onClose }) {
  const slots = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 30) {           // 30-min slots on mobile (cleaner)
      const val = `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`;
      let disabled = false;
      if (forDate === minDate && val < minTime) disabled = true;
      if (forDate === pickupDate && pickupTime && val <= pickupTime) disabled = true;
      slots.push({ val, disabled });
    }
  }
  return (
    <div style={{ padding:'12px 16px 24px', display:'grid', gridTemplateColumns:'repeat(3, 1fr)', gap:8 }}>
      {slots.map(s => (
        <button key={s.val} disabled={s.disabled}
          onClick={() => { if (!s.disabled) { onSelect(s.val); onClose(); } }}
          style={{
            border: `1.5px solid ${s.val === current ? T.ink : T.line}`,
            borderRadius:10, padding:'10px 4px', fontSize:13, fontWeight:600,
            cursor: s.disabled ? 'not-allowed' : 'pointer',
            background: s.val === current ? T.ink : T.white,
            color: s.val === current ? T.lime : s.disabled ? '#D0D0DC' : T.ink,
            transition:'all .15s',
          }}
        >{fmtTime(s.val)}</button>
      ))}
    </div>
  );
}

// ── Field Button ──────────────────────────────────────────────────────────
function FieldBtn({ label, active, onClick }) {
  return (
    <button type="button" onClick={onClick}
      style={{
        flex:1, padding:'13px 12px', borderRadius:12,
        border:`1.5px solid ${active ? T.ink : T.line}`,
        background: active ? T.ink : T.white,
        color: active ? T.lime : T.muted,
        fontSize:13, fontWeight:600, cursor:'pointer',
        textAlign:'left', whiteSpace:'nowrap',
        overflow:'hidden', textOverflow:'ellipsis',
        minWidth:0,
      }}
    >{label}</button>
  );
}

// ── Main Component ─────────────────────────────────────────────────────────
export default function RentalSearch() {
  const navigate = useNavigate();
  const { currentDate, minTime } = getCurrentDT();

  const [cities,     setCities]     = useState([]);
  const [loading,    setLoading]    = useState(false);
  const [error,      setError]      = useState('');
  const [submitting, setSubmitting] = useState(false);

  const [cityModal,  setCityModal]  = useState(false);
  const [selCity,    setSelCity]    = useState('');
  const [pDate,      setPDate]      = useState('');
  const [pTime,      setPTime]      = useState('');
  const [dDate,      setDDate]      = useState('');
  const [dTime,      setDTime]      = useState('');

  // Which bottom-sheet is open: null | 'pDate' | 'pTime' | 'dDate' | 'dTime'
  const [sheet, setSheet] = useState(null);

  const [pkgDur, setPkgDur] = useState('7');

  const API = useMemo(() => ({
    cities: `${BASE_URL}/api/cities/active`,
  }), []);

  const apiCall = useCallback(async url => {
    const r = await fetch(url, { headers:{ Accept:'application/json' } });
    if (!r.ok) throw new Error(`HTTP ${r.status}`);
    const d = await r.json();
    if (d.success === false) throw new Error(d.message);
    return d;
  }, []);

  const fetchCities = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const d = await apiCall(API.cities);
      setCities(Array.isArray(d?.data) ? d.data : Array.isArray(d) ? d : []);
    } catch(e) { setError('Could not load cities — ' + e.message); }
    finally { setLoading(false); }
  }, [API.cities, apiCall]);

  useEffect(() => { fetchCities(); window.scrollTo(0,0); }, []);

  const autoDropoff = (date, time) => {
    if (!date || !time) return;
    const dt = new Date(`${date}T${time}:00`);
    dt.setDate(dt.getDate() + 1);
    const pad = n => String(n).padStart(2,'0');
    setDDate(`${dt.getFullYear()}-${pad(dt.getMonth()+1)}-${pad(dt.getDate())}`);
    setDTime(`${pad(dt.getHours())}:${pad(dt.getMinutes())}`);
  };

  const selCityData = cities.find(c => c.id === selCity);
  const valid = selCity && pDate && pTime && dDate && dTime;

  const submit = async e => {
    e.preventDefault();
    if (!valid) return;
    setSubmitting(true);
    const p = new URLSearchParams({ startDate:pDate, endDate:dDate, cityId:selCity, page:'0', size:'12', pickupTime:pTime, dropoffTime:dTime });
    navigate(`/rental/bikes?${p}`);
    setSubmitting(false);
  };

  const getImgUrl = path => {
    if (!path) return null;
    if (path.startsWith('http')) return path;
    return `${BASE_URL}/uploads/${path.replace(/^\/?(uploads\/)?/,'')}`;
  };

  const openSheet = key => setSheet(key);
  const closeSheet = () => setSheet(null);

  return (
    <div style={{ minHeight:'100vh', fontFamily:"'DM Sans', sans-serif", background:T.surface }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=DM+Sans:ital,opsz,wght@0,9..40,400;0,9..40,500;0,9..40,700;0,9..40,900;1,9..40,400&family=Space+Grotesk:wght@700;900&display=swap');
        *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
        html { -webkit-text-size-adjust: 100%; }
        ::-webkit-scrollbar { width: 4px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: ${T.line}; border-radius: 4px; }

        @keyframes slideUp  { from { opacity:0; transform:translateY(24px); } to { opacity:1; transform:translateY(0); } }
        @keyframes sheetUp  { from { transform:translateY(100%); } to { transform:translateY(0); } }
        @keyframes marquee  { from { transform:translateX(0); } to { transform:translateX(-50%); } }
        @keyframes pulse    { 0%,100%{opacity:1;} 50%{opacity:.4;} }

        .pkg-card:hover  { transform:translateY(-4px) !important; box-shadow:0 16px 40px rgba(0,0,0,.10) !important; }
        .why-card:hover  { background:${T.ink} !important; }
        .why-card:hover .why-title  { color:${T.lime} !important; }
        .why-card:hover .why-sub    { color:rgba(255,255,255,.5) !important; }
        .rev-card:hover  { border-color:${T.lime} !important; }

        /* ── Responsive Grid Helpers ── */
        .hero-grid {
          display: grid;
          grid-template-columns: 1fr 420px;
          gap: 60px;
          align-items: center;
        }
        .pkg-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(240px, 1fr));
          gap: 20px;
        }
        .why-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
          gap: 12px;
        }
        .stats-grid {
          display: grid;
          grid-template-columns: repeat(4, 1fr);
          gap: 12px;
          margin-top: 40px;
        }
        .steps-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 2px;
        }
        .loc-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
          gap: 12px;
        }
        .rev-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
          gap: 16px;
        }
        .hero-stats {
          display: flex;
          gap: 32px;
        }
        .booking-card-wrap {
          background: ${T.white};
          border-radius: 24px;
          padding: 28px;
          box-shadow: 0 40px 100px rgba(0,0,0,.3);
          animation: slideUp .7s .1s ease both;
          opacity: 0;
          animation-fill-mode: forwards;
        }

        /* ── Mobile ── */
        @media (max-width: 768px) {
          .hero-grid {
            grid-template-columns: 1fr;
            gap: 32px;
          }
          .hero-stats { gap: 20px; flex-wrap: wrap; }
          .hero-stats > div { min-width: 60px; }
          .booking-card-wrap {
            padding: 20px 16px;
            border-radius: 20px;
            box-shadow: 0 20px 48px rgba(0,0,0,.18);
          }
          .pkg-grid { grid-template-columns: repeat(2, 1fr); gap: 12px; }
          .why-grid { grid-template-columns: repeat(2, 1fr); }
          .stats-grid { grid-template-columns: repeat(2, 1fr); }
          .steps-grid { grid-template-columns: repeat(2, 1fr); }
          .loc-grid   { grid-template-columns: repeat(2, 1fr); }
          .rev-grid   { grid-template-columns: 1fr; }
        }
        @media (max-width: 400px) {
          .pkg-grid { grid-template-columns: 1fr; }
          .steps-grid { grid-template-columns: 1fr; }
          .loc-grid   { grid-template-columns: 1fr; }
        }
      `}</style>

      {/* ── HERO ── */}
      <section style={{ background:T.ink, position:'relative', overflow:'hidden', minHeight:'auto', display:'flex', alignItems:'center' }}>
        <div style={{ position:'absolute', inset:0, backgroundImage:`linear-gradient(${T.slate} 1px, transparent 1px),linear-gradient(90deg,${T.slate} 1px,transparent 1px)`, backgroundSize:'60px 60px', opacity:.3 }}/>
        <div style={{ position:'absolute', top:-120, right:-80, width:500, height:500, background:T.lime, borderRadius:'50%', filter:'blur(120px)', opacity:.08 }}/>

        <div className="hero-grid" style={{ maxWidth:1400, margin:'0 auto', padding:'48px 20px 52px', position:'relative', zIndex:1, width:'100%' }}>

          {/* Left */}
          <div style={{ animation:'slideUp .6s ease both' }}>
            <div style={{ display:'inline-flex', alignItems:'center', gap:8, background:'rgba(200,242,60,.1)', border:`1px solid rgba(200,242,60,.3)`, borderRadius:40, padding:'6px 14px', marginBottom:20 }}>
              <span style={{ width:7, height:7, borderRadius:'50%', background:T.lime, display:'block', animation:'pulse 1.5s infinite' }}/>
              <span style={{ fontSize:11, fontWeight:700, color:T.lime, letterSpacing:1, textTransform:'uppercase' }}>Pune's Premium Bike Rentals</span>
            </div>

            <h1 style={{ fontFamily:"'Space Grotesk', sans-serif", fontSize:'clamp(36px,8vw,80px)', lineHeight:1.0, color:T.white, fontWeight:700, marginBottom:20 }}>
              Ride Pune<br/><span style={{ color:T.lime }}>Your Way.</span>
            </h1>

            <p style={{ fontSize:15, color:'rgba(255,255,255,.55)', lineHeight:1.6, maxWidth:440, marginBottom:32 }}>
              Scooters to sport bikes — book in 60 seconds. No hidden fees, fully insured, 5 pickup hubs across the city.
            </p>

            <div className="hero-stats">
              {[['500+','Bikes'],['10+','Years'],['50K+','Riders'],['4.8★','Google']].map(([n,l]) => (
                <div key={l}>
                  <div style={{ fontSize:22, fontWeight:900, color:T.lime }}>{n}</div>
                  <div style={{ fontSize:11, color:'rgba(255,255,255,.4)', fontWeight:500 }}>{l}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Booking Card */}
          <div className="booking-card-wrap">
            <div style={{ marginBottom:18 }}>
              <p style={{ fontSize:11, fontWeight:700, color:T.lime, letterSpacing:1.5, textTransform:'uppercase', marginBottom:4 }}>Book Your Ride</p>
              <h3 style={{ fontSize:18, fontWeight:800, color:T.ink }}>Start your journey</h3>
            </div>

            {error && (
              <div style={{ background:'#FFF0ED', border:'1px solid #FFD0C0', borderRadius:10, padding:'10px 14px', marginBottom:16, fontSize:13, color:'#C0392B', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <span>{error}</span>
                <button onClick={() => { setError(''); fetchCities(); }} style={{ background:'none', border:'none', color:'#C0392B', fontWeight:700, cursor:'pointer', fontSize:12 }}>Retry</button>
              </div>
            )}

            <form onSubmit={submit} style={{ display:'flex', flexDirection:'column', gap:14 }}>

              {/* City */}
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:T.muted, letterSpacing:1, textTransform:'uppercase', display:'block', marginBottom:6 }}>City</label>
                <button type="button" onClick={() => setCityModal(true)} disabled={loading}
                  style={{ width:'100%', padding:'13px 14px', borderRadius:12, border:`1.5px solid ${selCity?T.ink:T.line}`,
                    background:selCity?T.ink:T.white, color:selCity?T.lime:T.muted,
                    fontSize:13, fontWeight:600, cursor:'pointer', textAlign:'left' }}>
                  {loading ? '⏳ Loading…' : (selCityData?.cityName || '📍 Choose city')}
                </button>
              </div>

              {/* Pickup */}
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:T.muted, letterSpacing:1, textTransform:'uppercase', display:'block', marginBottom:6 }}>Pickup</label>
                <div style={{ display:'flex', gap:8 }}>
                  <FieldBtn
                    label={pDate ? fmtDate(pDate) : '📆 Date'}
                    active={!!pDate}
                    onClick={() => openSheet('pDate')}
                  />
                  <FieldBtn
                    label={pTime ? fmtTime(pTime) : '🕐 Time'}
                    active={!!pTime}
                    onClick={() => openSheet('pTime')}
                  />
                </div>
              </div>

              {/* Drop-off */}
              <div>
                <label style={{ fontSize:11, fontWeight:700, color:T.muted, letterSpacing:1, textTransform:'uppercase', display:'block', marginBottom:6 }}>
                  Drop-off {dDate && <span style={{ fontWeight:400 }}>(auto)</span>}
                </label>
                <div style={{ display:'flex', gap:8 }}>
                  <FieldBtn
                    label={dDate ? fmtDate(dDate) : '📆 Date'}
                    active={!!dDate}
                    onClick={() => openSheet('dDate')}
                  />
                  <FieldBtn
                    label={dTime ? fmtTime(dTime) : '🕐 Time'}
                    active={!!dTime}
                    onClick={() => openSheet('dTime')}
                  />
                </div>
              </div>

              {/* Submit */}
              <button type="submit" disabled={!valid || submitting}
                style={{ width:'100%', padding:'14px', borderRadius:12, border:'none',
                  background: valid ? T.ink : T.line,
                  color: valid ? T.lime : T.muted,
                  fontSize:14, fontWeight:800, cursor: valid?'pointer':'not-allowed',
                  letterSpacing:.5, marginTop:4, transition:'all .2s' }}>
                {submitting ? 'Searching…' : '🔍 FIND AVAILABLE BIKES →'}
              </button>
            </form>
          </div>
        </div>
      </section>

      {/* ── BOTTOM SHEETS for date/time ── */}
      {sheet === 'pDate' && (
        <BottomSheetModal title="Pickup Date" onClose={closeSheet}>
          <CalendarPicker
            onSelect={d => { setPDate(d); if (pTime) autoDropoff(d, pTime); }}
            minDate={currentDate} current={pDate} onClose={closeSheet}
          />
        </BottomSheetModal>
      )}
      {sheet === 'pTime' && (
        <BottomSheetModal title="Pickup Time" onClose={closeSheet}>
          <TimeList
            onSelect={t => { setPTime(t); autoDropoff(pDate, t); }}
            current={pTime} forDate={pDate} minDate={currentDate}
            minTime={minTime} onClose={closeSheet}
          />
        </BottomSheetModal>
      )}
      {sheet === 'dDate' && (
        <BottomSheetModal title="Drop-off Date" onClose={closeSheet}>
          <CalendarPicker
            onSelect={d => setDDate(d)}
            minDate={pDate || currentDate} current={dDate} onClose={closeSheet}
          />
        </BottomSheetModal>
      )}
      {sheet === 'dTime' && (
        <BottomSheetModal title="Drop-off Time" onClose={closeSheet}>
          <TimeList
            onSelect={t => setDTime(t)}
            current={dTime} forDate={dDate} minDate={currentDate}
            minTime={minTime} pickupDate={pDate} pickupTime={pTime} onClose={closeSheet}
          />
        </BottomSheetModal>
      )}

      {/* ── SCROLLING MARQUEE ── */}
      <div style={{ background:T.lime, overflow:'hidden', padding:'10px 0' }}>
        <div style={{ display:'flex', animation:'marquee 20s linear infinite', width:'max-content' }}>
          {['500+ Bikes','Instant Booking','Fully Insured','5 Hubs in Pune','Best Price Guarantee','24/7 Support'].flatMap(t=>[
            <span key={t+'1'} style={{ fontSize:12, fontWeight:800, color:T.ink, textTransform:'uppercase', letterSpacing:1.5, padding:'0 28px', whiteSpace:'nowrap' }}>{t}</span>,
            <span key={t+'2'} style={{ fontSize:12, color:T.ink, padding:'0 6px' }}>✦</span>,
          ]).concat(
            ['500+ Bikes','Instant Booking','Fully Insured','5 Hubs in Pune','Best Price Guarantee','24/7 Support'].flatMap(t=>[
              <span key={t+'a'} style={{ fontSize:12, fontWeight:800, color:T.ink, textTransform:'uppercase', letterSpacing:1.5, padding:'0 28px', whiteSpace:'nowrap' }}>{t}</span>,
              <span key={t+'b'} style={{ fontSize:12, color:T.ink, padding:'0 6px' }}>✦</span>,
            ])
          )}
        </div>
      </div>

      {/* ── PACKAGES SECTION ── */}
      <section style={{ padding:'64px 20px', background:T.white }}>
        <div style={{ maxWidth:1200, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <p style={{ fontSize:11, fontWeight:700, color:T.lime, letterSpacing:2, textTransform:'uppercase', background:T.ink, display:'inline-block', padding:'4px 14px', borderRadius:40, marginBottom:14 }}>Save Big</p>
            <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'clamp(26px,5vw,48px)', fontWeight:700, color:T.ink, marginBottom:8 }}>Long-Term Rental Deals</h2>
            <p style={{ color:T.muted, fontSize:15 }}>Rent 7+ days and save up to 30%</p>
          </div>

          {/* Duration Toggle */}
          <div style={{ display:'flex', justifyContent:'center', marginBottom:32 }}>
            <div style={{ display:'inline-flex', background:T.surface, borderRadius:14, padding:4 }}>
              {[['7','7 Days'],['15','15 Days'],['30','30 Days']].map(([k,l]) => (
                <button key={k} onClick={() => setPkgDur(k)}
                  style={{ padding:'10px 20px', borderRadius:10, border:'none', fontSize:13, fontWeight:700, cursor:'pointer',
                    background: pkgDur===k ? T.ink : 'transparent',
                    color: pkgDur===k ? T.lime : T.muted }}>
                  {l}
                </button>
              ))}
            </div>
          </div>

          <div className="pkg-grid">
            {BIKE_PACKAGES.map((bike) => {
              const p = bike.packs[pkgDur];
              const discPct = pkgDur==='7'?'10':pkgDur==='15'?'20':'30';
              return (
                <div key={bike.id} className="pkg-card"
                  style={{ borderRadius:20, overflow:'hidden', border:`1.5px solid ${T.line}`, transition:'transform .25s, box-shadow .25s', background:T.white }}>
                  <div style={{ position:'relative', height:160 }}>
                    <img src={bike.image} alt={bike.name}
                      style={{ width:'100%', height:'100%', objectFit:'cover' }}
                      onError={e=>{ e.target.src=DEFAULT_SVG; }}/>
                    <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(13,13,15,.8) 0%, transparent 60%)' }}/>
                    <div style={{ position:'absolute', top:10, right:10, background:T.lime, color:T.ink, fontSize:11, fontWeight:800, padding:'3px 9px', borderRadius:20 }}>
                      {discPct}% OFF
                    </div>
                  </div>
                  <div style={{ padding:16 }}>
                    <h3 style={{ fontSize:14, fontWeight:800, color:T.ink, marginBottom:10 }}>{bike.name}</h3>
                    <div style={{ display:'flex', alignItems:'baseline', gap:6, marginBottom:6 }}>
                      <span style={{ fontSize:22, fontWeight:900, color:T.ink }}>₹{p.price.toLocaleString()}</span>
                      <span style={{ fontSize:12, color:T.muted }}>/{pkgDur} days</span>
                    </div>
                    <div style={{ display:'flex', justifyContent:'space-between', fontSize:12, color:T.muted, padding:'8px 0', borderTop:`1px solid ${T.line}` }}>
                      <span>Save <strong style={{ color:T.teal }}>₹{p.save.toLocaleString()}</strong></span>
                      <span><strong style={{ color:T.ink }}>₹{p.day}</strong>/day</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ── HOW IT WORKS ── */}
      <section style={{ padding:'64px 20px', background:T.ink }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:48 }}>
            <p style={{ fontSize:11, fontWeight:700, color:T.lime, letterSpacing:2, textTransform:'uppercase', marginBottom:12 }}>Simple Process</p>
            <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'clamp(26px,5vw,44px)', fontWeight:700, color:T.white }}>Book in 4 Steps</h2>
          </div>

          <div className="steps-grid">
            {STEPS.map((s,i) => (
              <div key={i} style={{ padding:'28px 20px', borderLeft: i%2===1 ? `1px solid ${T.slate}` : 'none', borderTop: i>=2 ? `1px solid ${T.slate}` : 'none' }}>
                <div style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:42, fontWeight:900, color:T.slate, marginBottom:10, lineHeight:1 }}>{s.n}</div>
                <h3 style={{ fontSize:16, fontWeight:800, color:T.white, marginBottom:6 }}>{s.label}</h3>
                <p style={{ fontSize:13, color:'rgba(255,255,255,.4)' }}>{s.desc}</p>
              </div>
            ))}
          </div>

          <div style={{ textAlign:'center', marginTop:40 }}>
            <button onClick={() => window.scrollTo({ top:0, behavior:'smooth' })}
              style={{ padding:'14px 32px', borderRadius:12, background:T.lime, border:'none', color:T.ink, fontSize:14, fontWeight:800, cursor:'pointer', letterSpacing:.5 }}>
              Book Now →
            </button>
          </div>
        </div>
      </section>

      {/* ── WHY VEGATAS ── */}
      <section style={{ padding:'64px 20px', background:T.surface }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <p style={{ fontSize:11, fontWeight:700, color:T.ink, letterSpacing:2, textTransform:'uppercase', background:T.lime, display:'inline-block', padding:'4px 14px', borderRadius:40, marginBottom:14 }}>Why Us</p>
            <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'clamp(26px,5vw,44px)', fontWeight:700, color:T.ink }}>The Vegatas Difference</h2>
          </div>
          <div className="why-grid">
            {WHY.map((w,i) => (
              <div key={i} className="why-card"
                style={{ padding:'24px 16px', borderRadius:16, background:T.white, border:`1.5px solid ${T.line}`, textAlign:'center', cursor:'default', transition:'background .25s' }}>
                <div style={{ fontSize:26, marginBottom:10 }}>{w.icon}</div>
                <p className="why-title" style={{ fontSize:14, fontWeight:800, color:T.ink, marginBottom:4, transition:'color .25s' }}>{w.title}</p>
                <span className="why-sub" style={{ fontSize:12, color:T.muted, transition:'color .25s' }}>{w.sub}</span>
              </div>
            ))}
          </div>

          <div className="stats-grid">
            {[['10+','Years on Road'],['500+','Bikes'],['50K+','Riders'],['4.8★','Rating']].map(([v,l]) => (
              <div key={l} style={{ background:T.white, borderRadius:16, padding:'18px 14px', textAlign:'center', border:`1.5px solid ${T.line}` }}>
                <div style={{ fontSize:24, fontWeight:900, color:T.ink }}>{v}</div>
                <div style={{ fontSize:12, color:T.muted, marginTop:4 }}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── LOCATIONS ── */}
      <section style={{ padding:'64px 20px', background:T.ink }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:40 }}>
            <p style={{ fontSize:11, fontWeight:700, color:T.lime, letterSpacing:2, textTransform:'uppercase', marginBottom:12 }}>Pune Coverage</p>
            <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'clamp(26px,5vw,44px)', fontWeight:700, color:T.white }}>4 Pickup Hubs</h2>
          </div>
          <div className="loc-grid">
            {[
              { name:'Kothrud',      addr:'Suhas Society, Paud Rd',   hours:'7AM–9PM',  hq:true  },
              { name:'FC Road',      addr:'Phase 1, IT Park',          hours:'6AM–10PM', hq:false },
              { name:'Bavdhan',      addr:'Near D-Mart, Wakad Chowk', hours:'7AM–9PM',  hq:false },
              { name:'Kharadi Park', addr:'Lane 5, Near Osho Ashram', hours:'8AM–10PM', hq:false },
            ].map((loc,i) => (
              <div key={i} style={{ padding:18, borderRadius:16, border:`1.5px solid ${loc.hq?T.lime:T.slate}`, position:'relative', background:loc.hq?'rgba(200,242,60,.06)':'transparent' }}>
                {loc.hq && <span style={{ position:'absolute', top:10, right:10, background:T.lime, color:T.ink, fontSize:9, fontWeight:800, padding:'2px 8px', borderRadius:20, letterSpacing:1 }}>HQ</span>}
                <div style={{ fontSize:20, marginBottom:8 }}>📍</div>
                <h3 style={{ fontSize:14, fontWeight:800, color:T.white, marginBottom:4 }}>{loc.name}</h3>
                <p style={{ fontSize:12, color:'rgba(255,255,255,.4)', marginBottom:6 }}>{loc.addr}</p>
                <span style={{ fontSize:11, color:T.muted }}>🕐 {loc.hours}</span>
              </div>
            ))}
            <div style={{ padding:18, borderRadius:16, border:`1.5px dashed ${T.slate}`, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center', gap:10 }}>
              <div style={{ fontSize:24 }}>🗺️</div>
              <p style={{ fontSize:12, color:T.muted }}>Need directions?</p>
              <a href="https://maps.google.com" target="_blank" rel="noopener noreferrer"
                style={{ padding:'8px 18px', borderRadius:10, background:T.lime, color:T.ink, fontSize:12, fontWeight:700, textDecoration:'none' }}>
                Open Maps
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── REVIEWS ── */}
      <section style={{ padding:'64px 20px', background:T.surface }}>
        <div style={{ maxWidth:1100, margin:'0 auto' }}>
          <div style={{ textAlign:'center', marginBottom:12 }}>
            <p style={{ fontSize:11, fontWeight:700, color:T.ink, letterSpacing:2, textTransform:'uppercase', background:T.lime, display:'inline-block', padding:'4px 14px', borderRadius:40, marginBottom:14 }}>Reviews</p>
            <h2 style={{ fontFamily:"'Space Grotesk',sans-serif", fontSize:'clamp(26px,5vw,44px)', fontWeight:700, color:T.ink, marginBottom:8 }}>What Riders Say</h2>
            <div style={{ display:'inline-flex', alignItems:'center', gap:10, background:T.white, borderRadius:40, padding:'8px 20px', border:`1px solid ${T.line}`, marginBottom:36 }}>
              <span style={{ color:'#F59E0B', fontSize:16 }}>★★★★★</span>
              <span style={{ fontSize:18, fontWeight:900, color:T.ink }}>4.8</span>
              <span style={{ fontSize:13, color:T.muted }}>· 200+ Google Reviews</span>
            </div>
          </div>
          <div className="rev-grid">
            {REVIEWS.map((r,i) => (
              <div key={i} className="rev-card" style={{ background:T.white, borderRadius:16, padding:18, border:`1.5px solid ${T.line}`, transition:'border-color .2s' }}>
                <div style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
                  <div style={{ width:38, height:38, borderRadius:'50%', background:i%2===0?T.ink:T.surface, border:`2px solid ${i%2===0?T.lime:T.line}`, display:'flex', alignItems:'center', justifyContent:'center', fontSize:12, fontWeight:800, color:i%2===0?T.lime:T.ink, flexShrink:0 }}>
                    {r.name.split(' ').map(n=>n[0]).join('')}
                  </div>
                  <div style={{ minWidth:0 }}>
                    <div style={{ fontSize:13, fontWeight:700, color:T.ink }}>{r.name}</div>
                    <div style={{ fontSize:11, color:T.muted }}>{r.loc}</div>
                  </div>
                  <div style={{ marginLeft:'auto', fontSize:11, color:T.muted, flexShrink:0 }}>{r.ago}</div>
                </div>
                <div style={{ color:'#F59E0B', marginBottom:8, fontSize:13 }}>{'★'.repeat(r.stars)}</div>
                <p style={{ fontSize:13, color:T.slate, lineHeight:1.6 }}>{r.txt}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <BikeSlidingSection />
      <CarSlidingSection />

      {/* ── CITY MODAL ── */}
      {cityModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(13,13,15,.85)', backdropFilter:'blur(8px)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16 }}>
          <div style={{ background:T.white, borderRadius:24, width:'100%', maxWidth:880, maxHeight:'90vh', overflowY:'auto', boxShadow:'0 40px 100px rgba(0,0,0,.4)' }}>
            <div style={{ padding:'20px 20px', borderBottom:`1px solid ${T.line}`, display:'flex', alignItems:'center', justifyContent:'space-between', position:'sticky', top:0, background:T.white, zIndex:1 }}>
              <div>
                <h3 style={{ fontSize:18, fontWeight:800, color:T.ink }}>Select City</h3>
                <p style={{ fontSize:12, color:T.muted, marginTop:2 }}>Choose your pickup city</p>
              </div>
              <button onClick={() => setCityModal(false)} style={{ width:36, height:36, borderRadius:'50%', border:`1px solid ${T.line}`, background:'none', fontSize:18, cursor:'pointer', color:T.muted }}>✕</button>
            </div>
            <div style={{ padding:20 }}>
              {loading ? (
                <div style={{ textAlign:'center', padding:40, color:T.muted }}>Loading cities…</div>
              ) : cities.length === 0 ? (
                <div style={{ textAlign:'center', padding:40 }}>
                  <p style={{ color:T.muted, marginBottom:16 }}>No cities available</p>
                  <button onClick={fetchCities} style={{ padding:'10px 24px', borderRadius:10, background:T.ink, color:T.lime, border:'none', fontWeight:700, cursor:'pointer' }}>Refresh</button>
                </div>
              ) : (
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill, minmax(140px, 1fr))', gap:12 }}>
                  {cities.map(city => (
                    <button key={city.id} onClick={() => { setSelCity(city.id); setCityModal(false); }}
                      style={{ position:'relative', borderRadius:14, overflow:'hidden', border:`2px solid ${selCity===city.id?T.lime:T.line}`, cursor:'pointer', background:'none', padding:0, transition:'border-color .2s' }}>
                      <div style={{ aspectRatio:'4/3', background:T.surface }}>
                        {city.cityImage ? (
                          <img src={getImgUrl(city.cityImage)} alt={city.cityName} style={{ width:'100%', height:'100%', objectFit:'cover' }}
                            onError={e=>{ e.target.src=DEFAULT_SVG; }}/>
                        ) : (
                          <div style={{ width:'100%', height:'100%', background:T.ink, display:'flex', alignItems:'center', justifyContent:'center', fontSize:22 }}>🏙️</div>
                        )}
                        <div style={{ position:'absolute', inset:0, background:'linear-gradient(to top, rgba(13,13,15,.75) 0%, transparent 60%)' }}/>
                      </div>
                      <div style={{ position:'absolute', bottom:8, left:10, fontSize:13, fontWeight:800, color:T.white }}>{city.cityName}</div>
                      {selCity===city.id && (
                        <div style={{ position:'absolute', top:8, right:8, width:20, height:20, borderRadius:'50%', background:T.lime, display:'flex', alignItems:'center', justifyContent:'center', fontSize:11, fontWeight:900 }}>✓</div>
                      )}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
